import { createStore, produce, unwrap } from "solid-js/store";
import { type ASTNode, createAST, type DocASTNode, type ListASTNode } from "./toastmark/ast";
import { Parser } from "./toastmark/commonmark/blocks";
import type { InlineCut } from "./toastmark/commonmark/inlines";
import { type BlockNode, isCodeBlock, isList, isTable, type Node } from "./toastmark/commonmark/node";

const reLineEnding = /\r\n|\n|\r/g;

// Plain text ending in a space, parsed in front of the text after a cut in place of the text before
// it: the rest of the paragraph then parses as it would in place.
const STAND_IN = "x ";

// The store mirrors the parse, so an index one of them lacks is a bug rather than a state to handle.
const nth = <T>(items: readonly T[], index: number): T => {
  const item = items[index];
  if (item === undefined) {
    throw new Error(`streaming markdown: no node at index ${index} of ${items.length}`);
  }
  return item;
};

// Joins nodes parsed from a cut onto `carry`, the committed start of the text node the cut splits.
const joinCarry = (carry: string, nodes: ASTNode[]): ASTNode[] => {
  if (!carry) {
    return nodes;
  }
  const [first, ...rest] = nodes;
  return first?.type === "text"
    ? [{ ...first, literal: carry + first.literal }, ...rest]
    : [{ id: 0, type: "text", literal: carry, children: [] }, ...nodes];
};

const emptyDoc = (): DocASTNode => ({ type: "document", id: 0, literal: null, children: [] });

const childNodes = (node: Node) => {
  const nodes: Node[] = [];
  for (let child = node.firstChild; child; child = child.next) {
    nodes.push(child);
  }
  return nodes;
};

const sourcepos = (node: Node) => {
  if (!node.sourcepos) {
    throw new Error(`streaming markdown: parsed ${node.type} node has no source position`);
  }
  return node.sourcepos;
};
const startLine = (node: Node) => sourcepos(node)[0][0];
const endLine = (node: Node) => sourcepos(node)[1][0];

// The line that settles whether a block exists. A table starts on its header line but only exists
// once its delimiter row does, and it takes that header line from a paragraph above it.
const decidingLine = (node: Node) => startLine(node) + (isTable(node) ? 1 : 0);

// Whether nothing still to come can change `block`, given the block after it. Either that block is
// settled by finished lines alone, or a blank line lies between them and `block` can't continue past
// one: a list can take more items or indented lines after a blank, and an indented code block more
// indented lines.
const settled = (block: Node, next: Node, finished: number) =>
  decidingLine(next) < finished ||
  (startLine(next) > endLine(block) + 1 && !isList(block) && !(isCodeBlock(block) && !block.isFenced));

// The children of a table or list that commit one by one: body rows, or items.
const partNodes = (block: Node) =>
  isTable(block) ? (block.lastChild?.type === "tableBody" ? childNodes(block.lastChild) : []) : childNodes(block);
const partASTs = (node: ASTNode) =>
  node.type === "tableWrapper" ? nth(nth(node.children, 0).children, 1).children : node.children;

// Whether a block ends in a blank line, looking into the last item of a list the way the parser does
// when it decides a list is loose. The parser's own check caches its answer on the node, so a second
// question gets a wrong one.
const endsWithBlank = (block: Node | null): boolean =>
  !!block &&
  ((block as BlockNode).lastLineBlank ||
    ((block.type === "list" || block.type === "item") && endsWithBlank(block.lastChild)));

// One table or list along the live path, and the rows or items of it already committed.
type Level = {
  kept: number;
  // Whether the committed items alone make the list loose; blank lines never go away.
  loose: boolean;
  // Which child of the first uncommitted item is the next level down.
  head: number;
};

type Split = { head: string; kept: number; carry: string; closers: RegExp[] };

// Replaces `children[from..]` with `next`, writing into the stored nodes rather than swapping them
// so a re-parse repaints only what changed. `next` shares every unchanged node with the store, so
// the walk stops there. A node whose type changed is swapped whole: its component changes anyway,
// and fields only the old type carries (a heading's level) must not survive onto the new one.
const morphChildren = (children: ASTNode[], next: ASTNode[], from = 0) => {
  let index = from - 1;
  for (const newChild of next) {
    index++;
    const oldChild = children[index];
    if (oldChild && unwrap(oldChild) === newChild) {
      continue;
    }
    if (oldChild?.type !== newChild.type) {
      children[index] = newChild;
      continue;
    }
    // Assigning children through the store would deep-walk the whole new subtree on every level.
    Object.assign(oldChild, { ...newChild, children: oldChild.children });
    morphChildren(oldChild.children, newChild.children);
  }
  if (children.length > from + next.length) {
    children.splice(from + next.length);
  }
};

// Writes the parse of a path level's uncommitted rows or items into the store after its committed
// ones, going a level down inside the first uncommitted item while the path does.
const graft = (path: Level[], level: number, target: ASTNode, stored: ASTNode, node: Node) => {
  const { kept, loose, head } = nth(path, level);
  // Below the top, a list's first item is the line standing in for its committed items.
  const children = partNodes(node).slice(level > 0 && isList(node) ? 1 : 0);
  const storedParts = partASTs(stored);
  const targetParts = partASTs(target);
  if (level + 1 < path.length) {
    const storedItem = nth(storedParts, kept);
    const storedHead = nth(storedItem.children, head);
    const targetItem = nth(targetParts, kept);
    const itemChildren = childNodes(nth(children, 0));
    morphChildren(targetItem.children, [
      ...createAST(itemChildren.slice(0, head), storedItem.children),
      storedHead,
      ...createAST(itemChildren.slice(head + 1), storedItem.children.slice(head + 1)),
    ]);
    graft(path, level + 1, nth(targetItem.children, head), storedHead, nth(itemChildren, head));
    morphChildren(targetParts, createAST(children.slice(1), storedParts.slice(kept + 1)), kept + 1);
  } else {
    morphChildren(targetParts, createAST(children, storedParts.slice(kept)), kept);
  }
  if (isList(node)) {
    const targetListData = (target as ListASTNode).listData;
    if (!targetListData || !node.listData) {
      throw new Error("streaming markdown: parsed list has no list data");
    }
    targetListData.tight = !loose && node.listData.tight;
  }
};

/**
 * Creates a streaming markdown parser that can be used to render markdown
 * incrementally.
 *
 * @example
 * ```ts
 * const parser = createSolidStreamingMarkdown("# Hello");
 * parser.append(", world!");
 * parser.append("\nHow's it going?");
 * ```
 */
export const createSolidStreamingMarkdown = (initialMarkdown = "") => {
  const parserOptions = { extendedAutolinks: true };
  let parser: Parser | null = new Parser(parserOptions);
  const [doc, setDoc] = createStore<DocASTNode>(emptyDoc());

  // Only the live text is parsed on an append; everything before it is committed — nodes already in
  // the store that no text still to come can change. Block structure is decided line by line, so a
  // closed block stays closed, but the last line is still growing and can change what it starts: a
  // lone "-" may become a list item, a thematic break or a heading underline.
  //
  // A live table or list commits inside itself too, along `path`: rows on finished lines, and items
  // once the next item starts on a finished line. When the first uncommitted item holds a list or
  // table of its own, that one commits the same way, and so on down. `prefix` re-creates the context
  // the uncommitted rest parses under: a table's header and delimiter rows, the lines of each open
  // item up to its nested block, and the first line of each nested list, which stands in for the
  // items committed after it so the rest continues a list rather than opening one — an opening item
  // is held to different rules after a paragraph.
  let tail = "";
  let prefix = "";
  // The store index of the live block.
  let live = 0;
  let path: Level[] = [];

  // The live block, when it is the last one and a paragraph, commits inline too, up to a cut (see
  // InlineCut); from then on only the text after the cut is parsed, as a paragraph of its own behind
  // STAND_IN. The committed part is the first `kept` children of the stored paragraph plus `carry`,
  // the start of the text node the cut splits. `head` is the committed text: the paragraph is parsed
  // whole again once the text after the cut closes something before it, or stops parsing as one
  // paragraph — a later line can end it, or make it a heading or its last line a table header.
  let split: Split | null = null;

  // A live fenced code block commits its finished lines into `fence`, the start of its literal, and
  // parses the rest behind its opening line as `prefix`. One content line always stays in `tail`, so
  // the parsed literal joins straight on: a fence's literal drops only its final line break.
  // Top-level fences only; a fence inside a list item is still parsed whole from the item's start.
  let fence = "";

  // The inline nodes of `text` parsed as one paragraph behind `standIn`, without it; null when
  // `text` parses into anything else.
  const paragraphNodes = (activeParser: Parser, standIn: string, text: string, previous: readonly ASTNode[] = []) => {
    const blocks = childNodes(activeParser.parse(standIn + text));
    const [block] = blocks;
    if (blocks.length !== 1 || block?.type !== "paragraph") {
      return null;
    }
    const nodes = createAST(childNodes(block), previous);
    if (!standIn) {
      return nodes;
    }
    const [first, ...rest] = nodes;
    if (typeof first?.literal !== "string") {
      throw new Error("streaming markdown: a paragraph behind the stand-in must open with text");
    }
    const literal = first.literal.slice(standIn.length);
    return literal ? [{ ...first, literal }, ...rest] : rest;
  };

  // Moves the cut to `at` in `tail`, committing the text before it.
  const commit = (activeParser: Parser, activeSplit: Split, standIn: string, at: number, closers: RegExp[]) => {
    const segment = tail.slice(0, at);
    // A sentinel after the segment's final space keeps the paragraph from trimming it.
    const segmentNodes = paragraphNodes(activeParser, standIn, `${segment}x`);
    if (!segmentNodes) {
      throw new Error("streaming markdown: text before a cut no longer parses as one paragraph");
    }
    const nodes = joinCarry(activeSplit.carry, segmentNodes);
    const lastLiteral = nodes.at(-1)?.literal;
    if (typeof lastLiteral !== "string") {
      throw new Error("streaming markdown: text before a cut must end in text");
    }
    activeSplit.kept += nodes.length - 1;
    activeSplit.carry = lastLiteral.slice(0, -1);
    activeSplit.head += segment;
    activeSplit.closers.push(
      ...closers.filter((closer) => !activeSplit.closers.some((c) => c.source === closer.source))
    );
    tail = tail.slice(at);
  };

  // Where in `tail` the last parse puts a cut in `block`, or -1. Only a cut on the last line maps
  // back: the paragraph's text drops the indent of each line, but not the text after it.
  const cutAt = (cut: InlineCut | null, block: Node | null) => {
    if (cut?.block !== block || cut.subject.includes("\n", cut.at)) {
      return -1;
    }
    const at = tail.trimEnd().length - (cut.subject.length - cut.at);
    const lineStart = Math.max(tail.lastIndexOf("\n"), tail.lastIndexOf("\r")) + 1;
    // A line under the paragraph's first may still grow into a table's delimiter row, taking the
    // line above as its header, until it holds a character no delimiter row can. A tail with no line
    // break is on the line of the last cut, which was checked then.
    return lineStart === 0 || /[^|:\- \t]/.test(tail.slice(lineStart, at)) ? at : -1;
  };

  // Handles an append while a paragraph is split; false when it has to be parsed whole again.
  const appendAfterCut = (activeParser: Parser, activeSplit: Split) => {
    const { kept, carry, closers } = activeSplit;
    if (closers.some((closer) => closer.test(tail))) {
      return false;
    }
    // A later line that ends the paragraph, makes it a heading or its last line a table header does
    // the same behind the stand-in.
    const nodes = paragraphNodes(activeParser, STAND_IN, tail, nth(unwrap(doc).children, live).children.slice(kept));
    if (!nodes) {
      return false;
    }
    const block = activeParser.doc.firstChild;
    const cut = activeParser.inlineParser.cut;
    setDoc(produce((draft) => morphChildren(nth(draft.children, live).children, joinCarry(carry, nodes), kept)));
    const at = cutAt(cut, block);
    if (cut && at > 0) {
      commit(activeParser, activeSplit, STAND_IN, at, cut.closers);
    }
    return true;
  };

  const append = (markdown: string) => {
    if (!parser) {
      throw new Error("append() called on a disposed streaming markdown parser");
    }
    tail += markdown;
    if (split) {
      if (appendAfterCut(parser, split)) {
        return;
      }
      tail = split.head + tail;
      split = null;
    }
    const input = prefix + tail;
    const blocks = childNodes(parser.parse(input));
    const parsedLiteral = blocks[0]?.literal ?? "";
    if (fence) {
      nth(blocks, 0).literal = fence + parsedLiteral;
    }
    const starts = [0, ...Array.from(input.matchAll(reLineEnding), (match) => match.index + match[0].length)];
    const lines = (from: number, to: number) => input.slice(starts[from - 1], starts[to - 1]);
    // Lines before this one have ended. A text ending in a lone carriage return may still become a
    // CRLF, so that line hasn't.
    const finished = starts.length - (input.endsWith("\r") ? 1 : 0);

    const stored = unwrap(doc).children;
    const from = live + (path.length > 0 ? 1 : 0);
    const next = createAST(blocks.slice(from - live), stored.slice(from));
    setDoc(
      produce((draft) => {
        if (path.length > 0) {
          graft(path, 0, nth(draft.children, live), nth(stored, live), nth(blocks, 0));
        }
        morphChildren(draft.children, next, from);
      })
    );

    // A table right under a paragraph took the paragraph's last line as its header, and parses from
    // that line on only if the line would open a paragraph by itself: indented as code, or opening a
    // list at "2.", it merely continued the one above.
    const headerStandsAlone = (block: Node, table: Node) => {
      if (block.type !== "paragraph" || startLine(table) > endLine(block) + 1) {
        return true;
      }
      const reparsed = new Parser(parserOptions).parse(lines(startLine(table), startLine(table) + 2)).firstChild;
      return reparsed !== null && isTable(reparsed);
    };
    let first = 0;
    while (first < blocks.length - 1) {
      const current = nth(blocks, first);
      const following = nth(blocks, first + 1);
      if (!settled(current, following, finished) || (isTable(following) && !headerStandsAlone(current, following))) {
        break;
      }
      first++;
    }
    live += first;
    const block = blocks[first];
    if (!block) {
      return;
    }

    const nextPath: Level[] = [];
    const segments: string[] = [];
    let tailLine = startLine(block);
    let persisted = first === 0 && path.length > 0;
    let node: Node | undefined = block;
    // The lines of the enclosing item up to `node`, part of the prefix only if `node` becomes a level.
    let itemHead = "";
    // A path level has to be settled: a list or table whose first line is still growing may turn
    // out to be something else.
    while (node && (isList(node) || isTable(node)) && decidingLine(node) < finished) {
      const level = nextPath.length;
      const old = persisted ? path[level] : undefined;
      const parts = partNodes(node);
      const children = parts.slice(old && level > 0 && isList(node) ? 1 : 0);
      let committed = 0;
      while (
        isTable(node)
          ? committed < children.length && startLine(nth(children, committed)) < finished
          : committed < children.length - 1 && startLine(nth(children, committed + 1)) < finished
      ) {
        committed++;
      }
      const kept = (old?.kept ?? 0) + committed;
      // A table or nested list with nothing committed is simply parsed whole, from its own start or
      // its item's; a nested list's first line would otherwise stand in for nothing.
      if (kept === 0 && (level > 0 || isTable(node))) {
        break;
      }
      const loose =
        (old?.loose ?? false) ||
        (isList(node) &&
          children.slice(0, committed).some((item) => endsWithBlank(item) || childNodes(item).some(endsWithBlank)));
      if (isTable(node)) {
        nextPath.push({ kept, loose, head: 0 });
        segments.push(itemHead + lines(startLine(node), startLine(node) + 2));
        const row = children[committed];
        tailLine = row
          ? startLine(row)
          : committed > 0
            ? startLine(nth(children, committed - 1)) + 1
            : startLine(node) + 2;
        break;
      }
      const item = nth(children, committed);
      const itemChildren = childNodes(item);
      const deeper = !!old && committed === 0 && level + 1 < path.length;
      const head = deeper ? old.head : itemChildren.length - 1;
      nextPath.push({ kept, loose, head });
      segments.push(level > 0 ? itemHead + lines(startLine(nth(parts, 0)), startLine(nth(parts, 0)) + 1) : "");
      tailLine = startLine(item);
      node = itemChildren[head];
      if (!node) {
        break;
      }
      itemHead = lines(startLine(item), startLine(node));
      persisted = deeper;
    }
    path = nextPath;
    prefix = segments.join("");
    tail = input.slice(starts[tailLine - 1]);
    if (first > 0) {
      fence = "";
    }
    if (isCodeBlock(block) && block.isFenced) {
      const open = startLine(block);
      // The last line to commit: the fence's last finished content line, or the one before its
      // closing fence, stays live.
      const last = Math.min(finished, endLine(block)) - 2;
      const literal = first === 0 ? parsedLiteral : block.literal;
      if (literal === null) {
        throw new Error("streaming markdown: parsed fenced code block has no literal");
      }
      let end = -1;
      for (let line = open; line < last; line++) {
        end = literal.indexOf("\n", end + 1);
      }
      fence += literal.slice(0, end + 1);
      if (fence) {
        prefix = lines(open, open + 1);
        tail = input.slice(starts[Math.max(last, open)]);
      }
    }
    if (block.type === "paragraph" && first === blocks.length - 1) {
      const cut = parser.inlineParser.cut;
      const at = cutAt(cut, block);
      if (cut && at > 0) {
        split = { head: "", kept: 0, carry: "", closers: [] };
        commit(parser, split, "", at, cut.closers);
      }
    }
  };

  append(initialMarkdown);

  return {
    /**
     * Resets the markdown streaming parser to a clean slate.
     *
     * Useful when you want to reuse the instance but have to
     * render a new markdown doc instead of appending to an existing one.
     *
     * @example `reset()`
     */
    reset: () => {
      parser = new Parser(parserOptions);
      tail = "";
      prefix = "";
      live = 0;
      path = [];
      split = null;
      fence = "";
      setDoc(emptyDoc());
    },
    /**
     * Main function for adding streamed markdown to the parser and
     * triggering incremental compilations
     *
     * @example `append("# Some chunk of markdown")`
     */
    append,
    /**
     * Releases the parser and empties the document. The parser holds the live text and the
     * commonmark tree of its last parse, which are only needed to extend the document, so a
     * finished one costs nothing to drop.
     *
     * Emptying the store repaints anything still rendering `doc`, so swap the consumer over to a
     * `parseMarkdown` document first and dispose after. `append` throws afterwards.
     */
    dispose: () => {
      parser = null;
      tail = "";
      prefix = "";
      path = [];
      split = null;
      fence = "";
      setDoc(emptyDoc());
    },
    /**
     * The markdown document root.
     *
     * Stored as a solid-js store so be careful with destructuring / reactivity.
     */
    doc,
  };
};

/**
 * Parses a finished markdown document into a plain AST — no store, so nothing downstream is
 * reactive, and the parser is dropped on return rather than kept alive to extend the document.
 *
 * Feed the result to `SolidStreamingMarkdown`'s `static` prop. Text that is still arriving needs
 * `createSolidStreamingMarkdown` instead.
 */
export const parseMarkdown = (markdown: string): DocASTNode => ({
  ...emptyDoc(),
  children: createAST(childNodes(new Parser({ extendedAutolinks: true }).parse(markdown))),
});

// Structural shape shared by every ToastMark AST node — enough to walk the tree
// for plain text without pulling in the node class hierarchy.
interface WalkableNode {
  type: string;
  literal: string | null;
  children: WalkableNode[];
  // Extra fields that exist on specific node subtypes — declared optionally so
  // we can read them without casting through `any`.
  level?: number;
  destination?: string | null;
  listData?: { type?: "ordered" | "bullet"; start?: number; task?: boolean; checked?: boolean } | null;
}

// The assistant marks a grounded claim with an inline `<cite>{id}</cite>` token.
// Every surface that shows the markdown drops the token — the renderer swaps it
// for a citation chip, and the plain markdown renderer discards it — so it is a
// marker, not prose, and must not survive into an export the reader opens.
function isCitationMarker(node: WalkableNode): boolean {
  if (node.type !== "htmlInline") return false;
  const literal = node.literal ?? "";
  return literal.startsWith("<cite>") && literal.endsWith("</cite>");
}

// Each block's text with its trailing newlines taken off, empty ones dropped —
// a thematic break separates blocks but has no text of its own.
function plainBlocks(nodes: WalkableNode[], separator: string): string {
  return nodes
    .map((n) => nodeToPlainText(n).replace(/\n+$/, ""))
    .filter(Boolean)
    .join(separator);
}

// A plain-text list keeps its markers, numbering included, since without them
// steps and sub-points read as one run of lines — and a task item keeps its box,
// or a checklist pastes with nothing to say what is done. An item's later lines hang under
// its first, which is what indents a nested list under its parent.
function listToPlainText(list: WalkableNode): string {
  const ordered = list.listData?.type === "ordered";
  let n = list.listData?.start ?? 1;
  return list.children
    .map((item) => {
      const box = item.listData?.task ? (item.listData.checked ? "[x] " : "[ ] ") : "";
      const marker = `${ordered ? `${n++}. ` : "- "}${box}`;
      const body = plainBlocks(item.children, "\n").replace(/\n(?=[^\n])/g, `\n${" ".repeat(marker.length)}`);
      return `${marker}${body}\n`;
    })
    .join("");
}

function nodeToPlainText(node: WalkableNode): string {
  const renderChildren = () => node.children.map(nodeToPlainText).join("");

  if (isCitationMarker(node)) return "";

  switch (node.type) {
    case "text":
    case "code":
    case "htmlInline":
      return node.literal ?? "";

    case "codeBlock":
    case "htmlBlock":
    case "customBlock":
      return `${(node.literal ?? "").replace(/\n+$/, "")}\n`;

    case "linebreak":
    case "softbreak":
      return "\n";

    // Blocks keep a blank line between them, as they do on screen.
    case "document":
    case "blockQuote":
      return `${plainBlocks(node.children, "\n\n")}\n`;

    case "heading":
    case "paragraph":
      return `${renderChildren().replace(/\n+$/, "")}\n`;

    case "list":
      return listToPlainText(node);

    case "item":
      return `${plainBlocks(node.children, "\n")}\n`;

    // Plain text has nowhere to hide an address, so it follows the label — once,
    // when the label already is the address.
    case "link": {
      const label = renderChildren();
      const url = node.destination ?? "";
      return !url || url === label || url === `mailto:${label}` || url === `http://${label}`
        ? label
        : `${label} (${url})`;
    }

    case "tableRow":
      return `${node.children
        .filter((c) => c.type === "tableCell")
        .map((c) => nodeToPlainText(c).trim())
        .join("\t")}\n`;

    case "image":
    case "strong":
    case "emph":
    case "strike":
    case "customInline":
    case "table":
    case "tableWrapper":
    case "tableHead":
    case "tableBody":
    case "tableCell":
      return renderChildren();

    case "thematicBreak":
      return "\n";

    case "refDef":
    case "tableDelimRow":
    case "tableDelimCell":
      return "";

    default:
      return node.literal ?? renderChildren();
  }
}

export function stripMarkdownToPlainText(md: string): string {
  if (!md) return "";
  const parser = createSolidStreamingMarkdown(md);
  return nodeToPlainText(parser.doc)
    .replace(/\n{3,}/g, "\n\n")
    .replace(/^\n+|\n+$/g, "");
}

function escapeHtml(text: string): string {
  const div = typeof document !== "undefined" ? document.createElement("div") : null;
  if (div) {
    div.textContent = text;
    return div.innerHTML;
  }
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function escapeHtmlAttr(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Only http(s), mailto, and tel URLs are allowed through as-is. Anything else
// (javascript:, data:, etc.) is replaced with `#` so a malicious link can't
// embed an executable URI in the exported document.
function sanitizeUrl(url: string | null | undefined): string {
  if (!url) return "#";
  const trimmed = url.trim();
  if (/^(https?:|mailto:|tel:|#|\/|\.)/i.test(trimmed)) return trimmed;
  return "#";
}

function nodeToHtml(node: WalkableNode, inTableHead = false): string {
  const renderChildren = (head = inTableHead) => node.children.map((c) => nodeToHtml(c, head)).join("");

  if (isCitationMarker(node)) return "";

  switch (node.type) {
    case "text":
      return escapeHtml(node.literal ?? "");
    case "softbreak":
      return "\n";
    case "linebreak":
      return "<br/>";
    case "emph":
      return `<em>${renderChildren()}</em>`;
    case "strong":
      return `<strong>${renderChildren()}</strong>`;
    case "strike":
      return `<s>${renderChildren()}</s>`;
    case "code":
      return `<code>${escapeHtml(node.literal ?? "")}</code>`;
    case "codeBlock":
      return `<pre><code>${escapeHtml(node.literal ?? "")}</code></pre>`;
    case "link":
      return `<a href="${escapeHtmlAttr(sanitizeUrl(node.destination))}">${renderChildren()}</a>`;
    case "image": {
      const altText = node.children.map(nodeToPlainText).join("");
      return `<img src="${escapeHtmlAttr(sanitizeUrl(node.destination))}" alt="${escapeHtmlAttr(altText)}"/>`;
    }
    case "heading": {
      const level = Math.min(Math.max(node.level ?? 1, 1), 6);
      return `<h${level}>${renderChildren()}</h${level}>`;
    }
    case "paragraph":
      return `<p>${renderChildren()}</p>`;
    case "blockQuote":
      return `<blockquote>${renderChildren()}</blockquote>`;
    case "list": {
      const ordered = node.listData?.type === "ordered";
      const tag = ordered ? "ol" : "ul";
      const start =
        ordered && node.listData?.start && node.listData.start !== 1 ? ` start="${node.listData.start}"` : "";
      return `<${tag}${start}>${renderChildren()}</${tag}>`;
    }
    case "item":
      return `<li>${renderChildren()}</li>`;
    case "thematicBreak":
      return "<hr/>";
    case "table":
      return `<table border="1" cellspacing="0" cellpadding="6" style="border-collapse:collapse;">${renderChildren()}</table>`;
    case "tableHead":
      return `<thead>${renderChildren(true)}</thead>`;
    case "tableBody":
      return `<tbody>${renderChildren(false)}</tbody>`;
    case "tableRow":
      return `<tr>${renderChildren()}</tr>`;
    case "tableCell": {
      const tag = inTableHead ? "th" : "td";
      return `<${tag}>${renderChildren()}</${tag}>`;
    }
    case "tableDelimRow":
    case "tableDelimCell":
    case "refDef":
      return "";
    case "htmlBlock":
    case "htmlInline":
      // AI-generated responses may include raw HTML; escape it rather than
      // passing through so injected markup cannot reshape the Word document.
      return escapeHtml(node.literal ?? "");
    // A leaf block: its text is the literal, and it has no children to render.
    case "customBlock":
      return `<div>${escapeHtml((node.literal ?? "").replace(/\n+$/, ""))}</div>`;
    case "document":
    case "customInline":
      return renderChildren();
    default:
      return node.literal ? escapeHtml(node.literal) : renderChildren();
  }
}

export function markdownToHtml(md: string): string {
  if (!md) return "";
  const parser = createSolidStreamingMarkdown(md);
  return nodeToHtml(parser.doc);
}
